/**
 * SESSION ROTATION IN THE BROWSER (#601) — a password change on THIS browser.
 *
 * A password change with `revokeOtherSessions` revokes every session of the
 * account, this browser's included, and its response re-issues this browser's.
 * Two things follow for every tab of this browser (they share its cookies):
 *
 *   1. Until the response lands, requests still leave with the OLD cookie and
 *      are refused. Confirming such a 401 would send the old cookie too, read
 *      "signed out", and log the tab out — so the session-death detector asks
 *      `sessionRotationInFlight()` first. The flag reaches OTHER tabs through
 *      localStorage, with an expiry so a tab that dies mid-change cannot silence
 *      the detector for good.
 *   2. Credentials minted from the old session (preview sessions) are refused
 *      from then on. `announceSessionRotated()` tells every tab to re-mint them
 *      from the new session (`onSessionRotated`).
 *
 * Dependency-free and inert outside a browser, so both `apps/web` and feature
 * packages can import it.
 */

const ROTATING_UNTIL_KEY = 'papan-session-rotating-until'
const ROTATED_AT_KEY = 'papan-session-rotated-at'
const ROTATED_EVENT = 'papan:session-rotated'
/** Longer than any password change takes; bounds a tab that died mid-change. */
const ROTATION_FLAG_TTL_MS = 30_000

let rotationsInThisTab = 0

// This package typechecks without the DOM library (see client.ts), so the two
// browser globals used here are typed locally.
interface BrowserStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}
interface BrowserWindow {
  localStorage?: BrowserStorage
  addEventListener(type: string, listener: (event: { key?: string | null }) => void): void
  removeEventListener(type: string, listener: (event: { key?: string | null }) => void): void
  dispatchEvent(event: Event): boolean
}

function browserWindow(): BrowserWindow | null {
  const g = globalThis as { window?: BrowserWindow }
  return typeof g.window === 'object' && g.window !== null ? g.window : null
}

function storage(): BrowserStorage | null {
  try {
    return browserWindow()?.localStorage ?? null
  } catch {
    // Accessing localStorage throws where site data is blocked.
    return null
  }
}

/** Run `rotate` (the password change) with the session-death detector standing down in every tab. */
export async function whileRotatingSession<T>(rotate: () => Promise<T>): Promise<T> {
  rotationsInThisTab += 1
  try {
    storage()?.setItem(ROTATING_UNTIL_KEY, String(Date.now() + ROTATION_FLAG_TTL_MS))
  } catch { /* storage full or blocked: this tab is still covered by its own counter */ }
  try {
    return await rotate()
  } finally {
    rotationsInThisTab -= 1
    if (rotationsInThisTab === 0) {
      try { storage()?.removeItem(ROTATING_UNTIL_KEY) } catch { /* expires on its own */ }
    }
  }
}

/** Whether a password change is in flight in this tab or another tab of this browser. */
export function sessionRotationInFlight(): boolean {
  if (rotationsInThisTab > 0) return true
  try {
    return Number(storage()?.getItem(ROTATING_UNTIL_KEY)) > Date.now()
  } catch {
    return false
  }
}

/** Tell every tab of this browser the session was re-issued: re-mint what the old one minted. */
export function announceSessionRotated(): void {
  const win = browserWindow()
  if (!win) return
  // Other tabs hear the storage write; this tab does not, so it gets the event.
  try { storage()?.setItem(ROTATED_AT_KEY, String(Date.now())) } catch { /* this tab still re-mints */ }
  win.dispatchEvent(new Event(ROTATED_EVENT))
}

/** Call `listener` when this browser's session was re-issued, in this tab or another. Returns an unsubscribe. */
export function onSessionRotated(listener: () => void): () => void {
  const win = browserWindow()
  if (!win) return () => {}
  const onEvent = () => listener()
  const onStorage = (event: { key?: string | null }) => { if (event.key === ROTATED_AT_KEY) listener() }
  win.addEventListener(ROTATED_EVENT, onEvent)
  win.addEventListener('storage', onStorage)
  return () => {
    win.removeEventListener(ROTATED_EVENT, onEvent)
    win.removeEventListener('storage', onStorage)
  }
}
